import { useTranslation } from 'react-i18next';
import React, { useState, useEffect } from 'react';
import LiveAuctionNav from './LiveAuctionNav.js';
import { useParams, useSearchParams } from 'react-router-dom';
import AuctionSum from '../../utils/AuctionSum';
import Pagination from '../../utils/Pagination';
import DataTable from '../../utils/DataTable';
import DataCard from '../../utils/DataCard';
import ImageUtil from '../../utils/ImageUtil';
import SearchBarGraph from '../../utils/SearchBarGraph.js';
import { useGeneralInfo } from '../../utils/GeneralnfoContext.js';
import { axiosInstance } from '../../utils/axiosInterceptor.js';

const AuctionLiveLots = () => {
    const { t } = useTranslation();
    const { devices } = useGeneralInfo();
    const { auctionID } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    
    // Initialize state from URL parameters or defaults
    const [page, setPage] = useState(parseInt(searchParams.get('page')) || 1);
    const [pageSize, setPageSize] = useState(parseInt(searchParams.get('pageSize')) || 24);
    const [orderBy, setOrderBy] = useState(searchParams.get('orderBy') || 'LotID');
    const [hideClosedLots, setHideClosedLots] = useState(searchParams.get('hideClosedLots') || "inactive");
    const [search, setSearch] = useState(searchParams.get('search') || '');
    const [selectedOptions, setSelectedOptions] = useState(searchParams.get('selectedOptions') || '');
    
    const [showFullPageDiv, setShowFullPageDiv] = useState(false);
    const [selectedLot, setSelectedLot] = useState(null);
    const [sortDirection, setSortDirection] = useState('asc');
    const [filters, setFilters] = useState({});
    const [cacheItems, setCacheItems] = useState([]);
    const [itemBidHistory, setItemBidHistory] = useState([]);
    const apiDjangoUrl = process.env.REACT_APP_BIDDJANGO_URL;
    const tabs = [
        { label: 'Live Lots', value: `/admin/auctions/current/${auctionID}/livelots` },
        { label: 'Check In', value: `/admin/auctions/current/${auctionID}/liveRegistration` }
    ];

    // Function to update URL parameters
    const updateUrlParams = (newParams) => {
        const updatedParams = new URLSearchParams(searchParams);
        Object.entries(newParams).forEach(([key, value]) => {
            if (value !== null && value !== undefined && value !== '') {
                updatedParams.set(key, value);
            } else {
                updatedParams.delete(key);
            }
        });
        setSearchParams(updatedParams);
    };

    useEffect(() => {
        const fetchCacheData = () => {
            const cacheResponse = axiosInstance.post(`${apiDjangoUrl}/getAllItemsFromCacheAdmin/`, {
                auction_id: auctionID,
                page: page,
                page_size: pageSize,
                search: search,
                filter_status: hideClosedLots,
                order_by: orderBy,
            });
            cacheResponse.then((response) => {
                if (response.data && response.data.data) {
                    setCacheItems(response.data);
                } else {
                    console.error("No data found in cache response.");
                }
            }).catch((error) => {
                console.error("Error fetching items from cache:", error);
            });
        };

        // Initial fetch
        fetchCacheData();

        // Set up interval for polling every 10 seconds
        const interval = setInterval(fetchCacheData, 10000);

        // Cleanup interval on component unmount or dependency change
        return () => clearInterval(interval);
    }, [auctionID, page, pageSize, orderBy, hideClosedLots, search]);


    const openFullPageDiv = async (event, lot, origin) => {
        event.preventDefault(); // Prevent the default link behavior

        try {
            const requestData = {
                OnAuctionPKID: lot.OnAuctionPKID,
                auction_id: auctionID,
            };

            if (origin !== 'bids') {
                requestData.show_unwatched = false;
            }

            const itemData = await axiosInstance.post(`${apiDjangoUrl}/getBidlistsByOAPKID/`, requestData);
            setItemBidHistory(itemData.data.data); // Use .data.data for your API response
            console.log("Bid History Data:", itemData.data.data);

            // Check if dataBidHistory is defined and has the expected structure
            if (!itemBidHistory) {
                console.error("Bid history data is not available or has an unexpected structure.");
                return;
            }

            // Set the selected lot and show the full page div
            setSelectedLot(lot);
            setShowFullPageDiv(true);
        } catch (error) {
            console.error("Error fetching bid history data:", error);
        }
    };

    const closeFullPageDiv = () => {
        setShowFullPageDiv(false);
        setSelectedLot(null);
    };

    const handlePageChange = (newPage) => {
        setPage(newPage);
        updateUrlParams({ page: newPage });
    };

    const handlePageSizeChange = (newPageSize) => {
        setPageSize(newPageSize);
        setPage(1);
        updateUrlParams({ pageSize: newPageSize, page: 1 });
    };

    function formaCountDowntTime(seconds) {
        if (seconds <= 0) return t('Ended');

        const timeUnits = [
            { label: 'd', value: 86400 },
            { label: 'h', value: 3600 },
            { label: 'm', value: 60 },
            { label: 's', value: 1 }
        ];

        let remaining = seconds;

        const result = timeUnits
            .map(unit => {
                const val = Math.floor(remaining / unit.value);
                remaining %= unit.value;
                return { label: unit.label, val };
            })
            .filter((_, idx, arr) => {
                return arr.findIndex(item => item.val > 0) <= idx;
            })
            .map(item => `${item.val}${item.label}`)
            .join(' ');

        console.log("left time: ", seconds, result)
        return result || '0s';
    }

    const handleHeaderClick = (field) => {
        let sortField = field;
        let newSortDirection = 'asc';
        if (orderBy.includes(field)) {
            // If already sorted by this field, toggle between asc and desc
            newSortDirection = orderBy.startsWith('-') ? 'asc' : 'desc';
            sortField = orderBy.startsWith('-') ? field : '-' + field;
        }
        setOrderBy(sortField);
        setSortDirection(newSortDirection);
        updateUrlParams({ orderBy: sortField });
    };

    const handleHideClosedLots = (event) => {
        const status = hideClosedLots === "inactive" ? "active" : "inactive";
        setHideClosedLots(status);
        updateUrlParams({ hideClosedLots: status });
    };

    const handleSearch = (filter, searchTerm) => {
        if (filter) {
            setFilters(filter);
        } else {
            setFilters({});
        }

        setSearch(searchTerm);
        setPage(1);
        updateUrlParams({ search: searchTerm, page: 1 });
    };

    const handleReset = () => {
        setSearch('');
        setFilters({});
        setSelectedOptions('');
        setPage(1);
        setOrderBy('LotID');
        setHideClosedLots('inactive');
        setSortDirection('asc');
        setItemBidHistory([]);
        setSelectedLot(null);
        setShowFullPageDiv(false);
        updateUrlParams({ search: '', selectedOptions: '' , page: 1, orderBy: 'LotID', hideClosedLots: 'inactive' });
    };

    const handleSearchByChange = (newSearchBy, oldSearchBy) => {
        setSelectedOptions(newSearchBy);
        updateUrlParams({ selectedOptions: newSearchBy });
        if (newSearchBy === '') {
            setFilters((prevFilters) => {
                const { [`${oldSearchBy}Ilike`]: _, ...rest } = prevFilters;
                return rest;
            });
            return;
        }
        const newFilters = { ...filters, [`${newSearchBy}Ilike`]: search };
        delete newFilters[`${oldSearchBy}Ilike`];
        setFilters(newFilters)
    };


    const liveItemCheck = cacheItems?.data || [];
    const totalPageNum = cacheItems?.totalPageNum || 1;
    const totalCount = cacheItems?.totalCount || 0;

    const searchOptions = [
        { value: '', label: 'All' },
        { value: 'LotID', label: 'Lot ID' },
        { value: 'Lead', label: 'Lead' }
    ];

    let auctionHeaders = [
        { label: 'Lot', field: 'LotID' },
        { label: 'Time Left', field: 'TimeLeft' },
        { label: 'High', field: 'HighBid' },
        { label: 'Max', field: 'MaxBid' },
        { label: 'Bids', field: 'Bids' },
        { label: 'Watches', field: 'Watches' },
        { label: 'Views', field: 'Views' },
    ];

    if (devices <= 1) {
        auctionHeaders = [
            { label: 'Lot', field: 'LotID' },
            { label: 'HighBid MaxBid', field: 'HighBid' },
            { label: 'Winner', field: 'WinnerCustomerName' },
            { label: 'Bids', field: 'Bids' },
            { label: 'Time Left', field: 'TimeLeft' },
        ];
    }
    const renderAuctionRow = (lot) => (
        <>
            <td>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <div style={{ height: '9vh', width: '9vw' }}>
                        <ImageUtil
                            picPath={lot.Path}
                            alt={lot.Lead}
                        />
                    </div>
                    <div style={{ marginLeft: '10px', maxWidth: '25ch', wordWrap: 'break-word' }}>
                        <a href={`https://xyzauction.ca/product/${auctionID}/${lot.OriginalItem}/${lot.OnAuctionPKID}`} target="_blank" rel="noopener noreferrer">
                            {lot.OnAuctionPKID.LotID}{lot.LotIDString} ({lot.item}) - {lot.Lead}
                        </a>
                    </div>
                </div>
            </td>
            <td>
                <div className='bg-[#e9ebe8] rounded p-1 text-[#616161] text-center'>
                    {formaCountDowntTime(lot.TimeLeft)}
                </div>
            </td>
            <td>
                <span className='livelots-table-combine-highbid'>{lot.HighBid}</span>
                <div className='livelots-table-combine-winner'>
                    <a href={`/admin/CustomerDetail/${lot.CustomerID}`} target="_blank" rel="noopener noreferrer">
                        {lot.WinnerCustomerName}
                    </a>
                </div>
                {devices <= 1 && (<div className='livelots-table-combine-maxbid'>{lot.MaxBid}</div>)}
            </td>
            {devices > 1 && (<td>{lot.MaxBid}</td>)}
            <td>
                <a href="#" onClick={(event) => openFullPageDiv(event, lot, 'bids')}>
                    <div className="label label-warning label-xl font-semibold">
                        {lot.Bids}
                    </div>
                </a>
            </td>
            {devices > 1 && (<>
                <td>
                    <a href="#" onClick={(event) => openFullPageDiv(event, lot, 'watches')}>
                        <div className="label label-warning label-xl font-semibold">
                            {lot.Watches}
                        </div>
                    </a>
                </td>
                <td>{lot.Views}</td>
            </>)}
        </>
    );

    const renderAuctionCard = (item) => (
        <div className="flex gap-3 justify-between">
            {item?.Path && (<div className="w-[20%] w-max-[20%] aspect-[3/2]">
                <ImageUtil
                    picPath={item.Path}
                    alt={item.Lead}
                />
            </div>)}
            <div className="w-[calc(80%-0.75rem)]">
                <div className='text-[1.1rem] line-clamp-2 overflow-hidden text-ellipsis'>
                    <a href={`https://xyzauction.ca/product/${auctionID}/${item.OriginalItem}/${item.OnAuctionPKID}`} target="_blank" rel="noopener noreferrer">
                        {item.OnAuctionPKID.LotID}{item.LotIDString} ({item.item}) - {item.Lead}
                    </a>
                </div>
                <div className="text-[0.95rem] w-fit">
                    <strong>HighBid (MaxBid):</strong> {item.HighBid} ({item.MaxBid})
                </div>
                <div className="text-[0.95rem] w-fit">
                    <a href={`/admin/CustomerDetail/${item.CustomerID}`} target="_blank" rel="noopener noreferrer">
                        <strong>Winner:</strong> {item.WinnerCustomerName}
                    </a>
                </div>
                <div className="text-[0.95rem] w-fit">
                    <a href="#" onClick={(event) => openFullPageDiv(event, item, 'bids')}>
                        <div className="label label-warning label-xl font-semibold">
                            <strong>Bids:</strong>{item.Bids}
                        </div>
                    </a>
                </div>
                <div className="text-[0.95rem] w-fit">
                    <div className="label label-warning label-xl font-semibold">
                        <strong>Time Left:</strong>{formaCountDowntTime(item.TimeLeft)}
                    </div>
                </div>
            </div>
        </div>
    );

    const handleOrderByChange = (e) => {
        const newOrderBy = e.target.value;
        setOrderBy(newOrderBy);
        setPage(1);
        updateUrlParams({ orderBy: newOrderBy, page: 1 });
    };

    const sortOptions = [
        { value: 'Lot', label: t('Lot') },
        { value: '-Bids', label: t('Bid Count (high to low)') },
        { value: 'Bids', label: t('Bid Count (low to high)') },
        { value: 'HighBid', label: t('Bid CAD (high to low)') },
        { value: '-HighBid', label: t('Bid CAD (low to high)') },
        { value: '-MaxBid', label: t('Max Bid (high to low)') },
        { value: 'MaxBid', label: t('Max Bid (low to high)') },
        // { value: '-TotalCusMaxBid', label: t('Most Recent Bids') },
        { value: 'TimeLeft', label: t('Time Left') },
        { value: '-Watches', label: t('Watches (high to low)') },
        { value: 'Watches', label: t('Watches (low to high)') },
        { value: '-Views', label: t('Views (high to low)') },
        { value: 'Views', label: t('Views (low to high)') },
    ];

    return (
        <div className='main-container'>
            <LiveAuctionNav tabsList={tabs} />
            <div className='inner-contain-margin'>
                <AuctionSum auctionID={auctionID} />
                <div className='table-shadow-part'>
                    <div className='flex items-center justify-between mb-2 flex-wrap gap-3'>
                        <SearchBarGraph
                            onSearch={handleSearch}
                            onReset={handleReset}
                            searchTerm={search}
                            searchOptions={searchOptions}
                            selectedOptions={selectedOptions}
                            onSearchByChange={handleSearchByChange}
                        />
                        <div className="one-filter">
                            <label className='one-filter-label'>{t('Sort')}</label>
                            <select
                                value={orderBy}
                                onChange={handleOrderByChange}
                                className="ml-2 p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                {sortOptions.map(option => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className='flex gap-2'>
                                <input
                                    type="checkbox"
                                    checked={hideClosedLots === "active"}
                                    onChange={handleHideClosedLots}
                                />
                                {t('Hide Closed Lots')}
                            </label>
                        </div>
                    </div>
                    {devices > 1 ? (<DataTable
                        headers={auctionHeaders.map(header => ({
                            ...header,
                            label: `${t(header.label)} ${orderBy.includes(header.field) ? (sortDirection === 'asc' ? '▲' : '▼') : ''}`
                        }))}
                        data={liveItemCheck}
                        renderRow={renderAuctionRow}
                        loading={false}
                        onHeaderClick={handleHeaderClick}
                        containerClassName='livelots-table-outer-pagination table-for-ipad'
                    />)
                        : (<DataCard
                            headers={auctionHeaders}
                            data={liveItemCheck}
                            renderCard={renderAuctionCard}
                            loading={false}
                        />)}
                    <Pagination
                        page={page}
                        pageSize={pageSize}
                        totalPageNum={totalPageNum}
                        totalCount={totalCount}
                        onPageChange={handlePageChange}
                        onPageSizeChange={handlePageSizeChange}
                        containerClassName='pagination-container-sticky'
                    />
                </div>
            </div>
            {showFullPageDiv && selectedLot && (
                <div className="full-page-div">
                    <div className="full-page-content cus-history-detail-table">
                        <div className='bid-history-header only-product-version'>
                            <p>{t('PRODUCT BID HISTORY')}</p>
                            <button onClick={closeFullPageDiv} >{t('CLOSE')}</button>
                        </div>

                        <div className='product-history-bid-name-picture'>
                            <div className='product-title-link'>
                                <a href={`/admin/ItemDetailRay/${selectedLot.OriginalItem}?origin=${origin}`} target="_blank" rel="noopener noreferrer">
                                    {selectedLot.LotID} ({selectedLot.item}) - {selectedLot.Lead} 🔗
                                </a>
                            </div>
                            <div style={{ height: '20vh' }}>
                                <ImageUtil
                                    picPath={selectedLot.Path}
                                    alt={selectedLot.Lead}
                                />
                            </div>
                        </div>
                        <table style={{ backgroundColor: 'white', color: 'black', margin: '0 auto' }}>
                            <thead>
                                <tr>
                                    <th>{t('Customer')}</th>
                                    <th>{t('Status')}</th>
                                    <th>{t('Limit')}</th>
                                    <th>{t('HighBid')}</th>
                                    <th>{t('MaxBid')}</th>
                                    <th>{t('Bids')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {itemBidHistory?.length > 0 ? (
                                    itemBidHistory.map((lot, index) => (
                                        <tr key={index} style={{ backgroundColor: 'white' }}>
                                            <td>
                                                <a href={`/admin/CustomerDetail/${lot.CustomerID}`} target="_blank" rel="noopener noreferrer">
                                                    {lot.CustomerName}
                                                </a>
                                            </td>
                                            <td>
                                                {(() => {
                                                    switch (parseInt(lot.ItemStatus)) {
                                                        case 0: return t('OutBid');
                                                        case 1: return t('Pending');
                                                        case 2: return <div className='status-label Winning'>{t('Winning')}</div>;
                                                        case 3: return t('Decline');
                                                        case 4: return t('OnlyWatch');
                                                        default: return '';
                                                    }
                                                })()}
                                            </td>
                                            <td>{lot.BidLimit}</td>
                                            <td>{lot.CusHighBid}</td>
                                            <td>{lot.CusMaxBid}</td>
                                            <td>{lot.BidCount}</td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="6" style={{ textAlign: 'center' }}>{t('No data yet')}</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AuctionLiveLots;